import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, Truck, MapPin, ChevronLeft, ShieldCheck, Zap, FileText, ShoppingBag, X, Maximize2, Gift, ChevronDown, Mail, User, AlertCircle } from 'lucide-react';
import Header from '../components/common/Header';
import { toast } from 'react-toastify';
import { useCart } from '../contexts/CartContext';
import catalogService from '../services/catalogService';
import { getImageUrl, generateSlug, cleanHtmlContent } from '../utils/helpers';
import { cartService } from '../services/cartService';
import { customerService } from '../services/customerService';
import { ORDER_STATUS } from '../utils/constants'; 
import itemService from '../services/itemService';
import Footer from '../components/common/Footer';

const PromotionAccordion = ({ promo }) => {
    const [isOpen, setIsOpen] = useState(false);
    const promoName = typeof promo === 'string' ? promo : (promo.saleName || promo.name);
    
    if (!promoName) return null;

    return (
        <div className="flex flex-col border border-orange-200/60 rounded-lg overflow-hidden bg-gradient-to-r from-orange-50 to-white w-full mb-1.5 last:mb-0 transition-all shadow-sm">
            <button
                type="button"
                onClick={(e) => { 
                    e.preventDefault(); 
                    e.stopPropagation(); 
                    if(promo.description) setIsOpen(!isOpen); 
                }}
                className={`flex items-center justify-between gap-2 px-2.5 py-2 w-full text-left transition-colors ${promo.description ? 'hover:bg-orange-50 cursor-pointer' : 'cursor-default'}`}
            >
                <div className="flex items-start gap-1.5 text-xs text-tnc-orange w-full min-w-0">
                    <Gift size={14} className="shrink-0 text-tnc-orange mt-0.5" />
                    <span className="font-bold leading-snug line-clamp-2">{promoName}</span>
                </div>
                {promo.description && (
                    <ChevronDown size={14} className={`text-tnc-orange shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                )}
            </button>
            
            {isOpen && promo.description && (
                <div className="px-2.5 pb-2.5 pt-0.5 text-[11px] text-gray-700 bg-white border-t border-orange-100">
                    <div 
                        className="html-content mt-1.5 [&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-md"
                        dangerouslySetInnerHTML={{ __html: String(promo.description).replace(/&nbsp;/g, ' ') }} 
                    />
                </div>
            )}
        </div>
    );
};

const CheckoutPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { fetchCart } = useCart();
    
    const [loading, setLoading] = useState(false);
    const [note, setNote] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    
    const [shippingPolicyHtml, setShippingPolicyHtml] = useState('');
    const [isLoadingShipping, setIsLoadingShipping] = useState(true);

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [isLoadingPayment, setIsLoadingPayment] = useState(true);

    const [previewImage, setPreviewImage] = useState(null);
    const checkoutData = location.state;

    useEffect(() => {
        if (!checkoutData || !checkoutData.items || checkoutData.items.length === 0) {
            navigate('/cart');
        }
    }, [checkoutData, navigate]);

    useEffect(() => {
        const fetchMethods = async () => {
            setIsLoadingShipping(true);
            setIsLoadingPayment(true);
            try {
                const configRes = await catalogService.getSystemConfig().catch(() => null);
                const actualConfig = configRes?.data || configRes;
                if (actualConfig && actualConfig.shipping_info) {
                    setShippingPolicyHtml(actualConfig.shipping_info);
                }

                const payRes = await customerService.getPublicPaymentMethods();
                const payData = Array.isArray(payRes?.data) ? payRes.data : (Array.isArray(payRes) ? payRes : []);
                setPaymentMethods(payData);
                if (payData.length > 0) setSelectedPayment(payData[0]);

            } catch (error) {
                console.error("Lỗi tải cấu hình thanh toán/giao hàng", error);
            } finally {
                setIsLoadingShipping(false);
                setIsLoadingPayment(false);
            }
        };
        fetchMethods();
    }, []);

    if (!checkoutData) return null;

    const formatPrice = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);

    const subTotal = checkoutData.items.reduce((sum, item) => sum + (item.activePrice * item.quantity), 0);
    const totalItemQuantity = checkoutData.items.reduce((sum, item) => sum + Number(item.quantity), 0);
    const finalTotal = subTotal;

    const executeOrder = async () => {
        setShowConfirmModal(false);
        setLoading(true);
        try {
            const ordersRes = await customerService.getMyOrders().catch(() => ({ data: [] }));
            const allOrders = Array.isArray(ordersRes?.data) ? ordersRes.data : (Array.isArray(ordersRes) ? ordersRes : []);
            const activeOrders = allOrders.filter(order => {
                const status = order.status !== undefined ? order.status : order.orderStatus;
                return !(status === ORDER_STATUS.DELIVERED || status === 4 || status === ORDER_STATUS.CANCELLED || status === 5);
            });

            const boughtQuantities = {};
            activeOrders.forEach(order => {
                const orderItems = order.items || order.orderDetails || [];
                orderItems.forEach(orderItem => {
                    const itemId = orderItem.item?.itemId || orderItem.item?.id || orderItem.itemId || orderItem.id;
                    if (itemId) {
                        if (!boughtQuantities[itemId]) boughtQuantities[itemId] = 0;
                        boughtQuantities[itemId] += Number(orderItem.quantity || 1);
                    }
                });
            });

            for (const item of checkoutData.items) {
                const productDetail = await itemService.getItemDetail(item.id).catch(() => null);
                const dataObj = productDetail?.data || productDetail || {};
                const maxPurchaseLimit = dataObj.itemQuantity ?? dataObj.quantity ?? 0;
                const requestedTotalQty = Number(item.quantity) + (boughtQuantities[item.id] || 0);

                if (requestedTotalQty > maxPurchaseLimit) {
                    toast.error(`Sản phẩm "${item.name}" giới hạn mua tối đa ${maxPurchaseLimit} chiếc.`);
                    setLoading(false);
                    return;
                }
            }

            const orderPayload = {
                user: {
                    customerName: checkoutData.address.receiverName,
                    phone: checkoutData.address.receiverPhone,
                    email: checkoutData.address.receiverEmail, 
                    address: `${checkoutData.address.specificAddress}, ${checkoutData.address.ward}, ${checkoutData.address.province}`,
                    note: note, 
                    shippingFee: 0, 
                    shippingMethodId: null, 
                    paymentMethodId: selectedPayment.id 
                },
                items: checkoutData.items.map(item => ({
                    id: item.id,
                    price: item.activePrice, 
                    quantity: item.quantity,
                    isFlashSale: item.isFlashSale 
                }))
            };

            await customerService.createOrder(orderPayload);
            await Promise.all(checkoutData.items.map(item => cartService.removeFromCart(item.cartItemId)));
            
            toast.success("Đặt hàng thành công! 🎉");
            await fetchCart(); 
            navigate('/my-orders'); 
            
        } catch (error) {
            toast.error(error.response?.data?.message || "Có lỗi xảy ra khi đặt hàng!");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />
            <main className="container mx-auto px-4 py-8 max-w-6xl flex-grow">
                <button onClick={() => navigate('/cart')} className="flex items-center gap-2 text-gray-500 hover:text-tnc-orange mb-6 transition-colors font-medium">
                    <ChevronLeft size={20}/> Quay lại giỏ hàng
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* CỘT TRÁI - CHIẾM 2 PHẦN */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Khối Sản phẩm */}
                        <div className="bg-gradient-to-r from-orange-50 to-white p-6 rounded-2xl shadow-sm border border-orange-200/60">
                            <h2 className="flex items-center gap-2 font-bold text-lg text-gray-800 mb-4">
                                <ShoppingBag className="text-tnc-orange" size={22}/> Sản phẩm thanh toán
                            </h2>
                            <div className="divide-y divide-orange-100">
                                {checkoutData.items.map((item) => {
                                    const isSale = !item.isFlashSale && item.activePrice < item.originalPrice;
                                    const priceColorClass = item.isFlashSale ? 'text-tnc-orange' : (isSale ? 'text-tnc-orange' : 'text-[#002855]');
                                    const rowClass = item.isFlashSale 
                                        ? "py-4 flex gap-4 items-start bg-gradient-to-r from-orange-50 to-white px-3 -mx-3 rounded-lg border border-orange-200/60 mb-2 shadow-sm" 
                                        : "py-4 flex gap-4 items-start px-3 -mx-3 border-b border-orange-100 last:border-0";

                                    return (
                                        <div key={item.id} className={rowClass}>
                                            <Link target="_blank" to={`/san-pham/${generateSlug(item.name)}-id${item.id}`} className="relative block shrink-0 group">
                                                <div className="w-20 h-20 bg-white rounded-lg border border-gray-200 p-1 group-hover:border-tnc-orange transition-colors">
                                                    <img src={getImageUrl(item.image)} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" />
                                                </div>
                                                {item.isFlashSale && <div className="absolute -top-2 -right-2 bg-tnc-orange text-white rounded-full p-1 shadow-md"><Zap size={10} fill="currentColor"/></div>}
                                            </Link>
                                            <div className="flex-1 min-w-0">
                                                <Link target="_blank" to={`/san-pham/${generateSlug(item.name)}-id${item.id}`} className="font-bold text-sm md:text-base text-gray-800 line-clamp-2 hover:text-tnc-orange transition-colors leading-snug">{item.name}</Link>
                                                <div className="text-xs text-gray-500 mt-1 mb-1.5">Mã SP: <span className="font-mono text-blue-600 font-bold">{item.itemCode}</span></div>
                                                <div className="flex items-center gap-2 mt-1.5 mb-2">
                                                    <p className="text-sm font-bold text-gray-600">SL: {item.quantity}</p>
                                                    {item.isFlashSale ? <span className="text-[10px] text-tnc-orange font-bold bg-orange-100 px-1.5 py-0.5 rounded">Flash Sale</span> : (isSale && <span className="text-[10px] text-tnc-orange font-bold bg-orange-100 px-1.5 py-0.5 rounded">Giảm giá</span>)}
                                                </div>
                                                {!item.isFlashSale && item.promotions?.length > 0 && (
                                                    <div className="mt-1 flex flex-col gap-1.5 pr-4">{item.promotions.map((p, idx) => <PromotionAccordion key={idx} promo={p} />)}</div>
                                                )}
                                            </div>
                                            <div className="text-right shrink-0 flex flex-col items-end">
                                                <span className={`font-bold text-base block ${priceColorClass}`}>{formatPrice(item.activePrice * item.quantity)}</span>
                                                {item.activePrice < item.originalPrice && <span className={`text-xs mt-0.5 line-through block ${item.isFlashSale ? 'text-orange-300' : 'text-gray-400'}`}>{formatPrice(item.originalPrice * item.quantity)}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Thông tin người nhận */}
                        <div className="bg-gradient-to-r from-orange-50 to-white p-6 rounded-2xl shadow-sm border border-orange-200/60">
                            <h2 className="flex items-center gap-2 font-bold text-lg text-gray-800 mb-4">
                                <User className="text-tnc-orange" size={22}/> Thông tin người nhận
                            </h2>
                            <div className="p-4 bg-white border border-orange-200/60 rounded-xl shadow-sm space-y-3">
                                <div className="flex flex-wrap items-center gap-3">
                                    <span className="font-bold text-gray-800">{checkoutData.address.receiverName}</span>
                                    <span className="text-gray-300">|</span>
                                    <span className="font-bold text-gray-800">{checkoutData.address.receiverPhone}</span>
                                    {checkoutData.address.receiverEmail && (
                                        <div className="flex items-center gap-1.5 text-blue-600 font-medium text-sm ml-auto">
                                            <Mail size={14} className="text-gray-400"/> {checkoutData.address.receiverEmail}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-start gap-2 pt-2 border-t border-orange-50">
                                    <MapPin size={18} className="text-tnc-orange shrink-0 mt-0.5"/>
                                    <p className="text-sm text-gray-600 leading-relaxed italic">
                                        {checkoutData.address.specificAddress}, {checkoutData.address.ward}, {checkoutData.address.province}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Phương thức thanh toán */}
                        <div className="bg-gradient-to-r from-orange-50 to-white p-6 rounded-2xl shadow-sm border border-orange-200/60">
                            <h2 className="flex items-center gap-2 font-bold text-lg text-gray-800 mb-4">
                                <CreditCard className="text-tnc-orange" size={22}/> Phương thức thanh toán
                            </h2>
                            {isLoadingPayment ? <p className="italic text-gray-400 text-sm">Đang tải...</p> : (
                                <div className="space-y-4">
                                    {paymentMethods.map((method) => {
                                        const isSelected = selectedPayment?.id === method.id;
                                        return (
                                            <label key={method.id} className={`flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all ${isSelected ? 'border-tnc-orange bg-orange-50/50 shadow-sm' : 'border-orange-200/60 bg-white'}`}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <input type="radio" checked={isSelected} onChange={() => setSelectedPayment(method)} className="w-4 h-4 text-tnc-orange" />
                                                        <p className="font-bold text-gray-800 text-sm sm:text-base">{method.name}</p>
                                                    </div>
                                                    <div className="p-1.5 bg-white rounded-md border border-orange-100 shrink-0">
                                                        {method.imageUrl ? <img alt={method.name} src={getImageUrl(method.imageUrl)} className="w-6 h-6 object-contain" /> : <CreditCard size={24} className="text-tnc-orange" />}
                                                    </div>
                                                </div>
                                                {isSelected && (method.imageUrl || method.content) && (
                                                    <div className="mt-4 pt-4 border-t border-orange-200/60 flex flex-col items-center animate-in fade-in slide-in-from-top-1">
                                                        {method.imageUrl && (
                                                            <div className="mb-4 flex flex-col items-center w-full" onClick={() => setPreviewImage(getImageUrl(method.imageUrl))}>
                                                                <p className="text-xs font-bold text-gray-500 mb-2 uppercase">Mã QR Thanh toán</p>
                                                                <div className="relative cursor-zoom-in">
                                                                    <img alt="QR Thanh toán" src={getImageUrl(method.imageUrl)} className="w-40 h-40 object-contain rounded-lg border bg-white p-2" />
                                                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/5 rounded-lg transition-opacity"><Maximize2 size={16}/></div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {method.content && <div className="text-sm text-gray-700 bg-white p-3 rounded-lg border border-orange-100 w-full text-center shadow-sm"><p className="whitespace-pre-wrap leading-relaxed">{method.content}</p></div>}
                                                    </div>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CỘT PHẢI - CHIẾM 1 PHẦN */}
                    <div className="lg:col-span-1">
                        <div className="space-y-6 sticky top-24">
                            <div className="bg-gradient-to-r from-orange-50 to-white p-6 rounded-2xl shadow-sm border border-orange-200/60">
                                <h2 className="flex items-center gap-2 font-bold text-lg text-gray-800 mb-4"><FileText className="text-tnc-orange" size={22}/> Ghi chú đơn hàng</h2>
                                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows="3" placeholder="Ghi chú thêm về đơn hàng..." className="w-full px-4 py-3 border border-orange-200/60 rounded-xl text-sm focus:border-tnc-orange outline-none resize-none bg-white transition-all focus:shadow-md"></textarea>
                            </div>
                            {/* 👉 ĐÃ CHUYỂN: Khối Thông tin giao hàng sang đây */}
                            <div className="bg-gradient-to-r from-orange-50 to-white p-6 rounded-2xl shadow-sm border border-orange-200/60">
                                <h2 className="flex items-center gap-2 font-bold text-lg text-gray-800 mb-4">
                                    <Truck className="text-tnc-orange" size={22}/> Thông tin giao hàng
                                </h2>
                                {isLoadingShipping ? (
                                    <p className="text-xs text-gray-400 italic">Đang tải...</p>
                                ) : (
                                    <div className="bg-white p-4 rounded-xl border border-orange-100 shadow-inner max-h-[300px] overflow-y-auto">
                                        {shippingPolicyHtml ? (
                                            <div className="text-[14px] text-gray-700 leading-relaxed html-content [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_strong]:font-bold" dangerouslySetInnerHTML={{ __html: cleanHtmlContent(shippingPolicyHtml) }} />
                                        ) : <p className="text-xs text-gray-600">Phí giao hàng sẽ được nhân viên liên hệ xác nhận sau.</p>}
                                    </div>
                                )}
                            </div>

                            <div className="bg-gradient-to-r from-orange-50 to-white p-6 rounded-2xl shadow-md border border-orange-200/60">
                                <h2 className="font-bold text-xl text-gray-800 mb-6 pb-4 border-b border-orange-200/60">Tổng đơn hàng</h2>
                                <div className="space-y-4 mb-6">
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span>Tạm tính ({totalItemQuantity} món):</span>
                                        <span className="font-medium text-gray-900">{formatPrice(subTotal)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Phí vận chuyển:</span>
                                        <span className="text-[10px] font-bold bg-orange-100 text-orange-700 px-2 py-1 rounded border border-orange-200">ĐANG CHỜ XÁC NHẬN</span>
                                    </div>
                                    <div className="flex justify-between items-center pt-4 border-t border-dashed border-orange-200/60">
                                        <span className="font-bold text-gray-800 text-lg">Tổng cộng:</span>
                                        <span className="font-extrabold text-2xl text-tnc-orange">{formatPrice(finalTotal)}</span>
                                    </div>
                                    <div className="mt-2 flex items-center gap-1.5 text-[10px] text-gray-400">
                                        <ShieldCheck size={14}/> <span>Giao dịch bảo mật & an toàn</span>
                                    </div>
                                </div>
                                <button onClick={() => setShowConfirmModal(true)} disabled={loading || !selectedPayment} className="w-full bg-tnc-orange hover:bg-tnc-orange-hover text-white font-bold py-4 rounded-xl shadow-lg transition-all uppercase flex items-center justify-center gap-2 tracking-wide disabled:bg-gray-300">
                                    {loading ? "Đang xử lý..." : "Xác nhận đặt hàng"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Modal Confirm */}
            {showConfirmModal && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-[24px] w-full max-w-md shadow-2xl overflow-hidden border border-orange-100">
                        <div className="bg-gradient-to-r from-orange-500 to-tnc-orange p-6 text-center">
                            <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3"><AlertCircle size={32} className="text-white" /></div>
                            <h3 className="text-white font-bold text-xl">Xác nhận đơn hàng</h3>
                        </div>
                        <div className="p-8 text-center">
                            <p className="text-gray-600 mb-6 leading-relaxed">Bạn chắc chắn muốn đặt đơn hàng trị giá <br/><strong className="text-tnc-orange text-2xl">{formatPrice(finalTotal)}</strong>?</p>
                            <div className="grid grid-cols-2 gap-4">
                                <button onClick={() => setShowConfirmModal(false)} className="py-3 px-6 border-2 border-gray-100 rounded-xl text-gray-500 font-bold hover:bg-gray-50 transition-colors">Hủy bỏ</button>
                                <button onClick={executeOrder} className="py-3 px-6 bg-tnc-orange text-white rounded-xl font-bold hover:bg-tnc-orange-hover shadow-lg transition-all">Xác nhận</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Image */}
            {previewImage && (
                <div className="fixed inset-0 bg-black/90 z-[400] flex items-center justify-center p-4" onClick={() => setPreviewImage(null)}>
                    <button className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"><X size={40} /></button>
                    <img alt="Preview" src={previewImage} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl animate-in zoom-in-95" onClick={(e) => e.stopPropagation()} />
                </div>
            )}
            
            <Footer />
        </div>
    );
};

export default CheckoutPage;